import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLForms = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Forms & Input Mastery</div>
                <h1>Form Validation & Attributes</h1>
                <p className="hero-lead">
                    Master HTML5 form validation with built-in attributes and constraints. Learn how to create
                    user-friendly forms with client-side validation before a single line of JavaScript.
                </p>
            </div>

            <div className="content-section">
                <h2>Why HTML5 Validation Matters</h2>
                <p>
                    HTML5 introduced powerful built-in form validation that works without JavaScript. This provides:
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Instant Feedback</h3>
                        <p>Users get immediate validation messages as they fill out forms</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Mobile-Friendly</h3>
                        <p>Specialized input types trigger appropriate mobile keyboards</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Security Layer</h3>
                        <p>Client-side validation as first defense (always validate server-side too!)</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <p>Screen readers announce validation errors automatically</p>
                    </div>
                </div>

                <Warning><strong> Critical Security Note:</strong> HTML5 validation is client-side only and can be bypassed.
                    <em>Always validate on the server</em> for security. Client-side validation is for user experience, not security.</Warning>
            </div>

            <div className="content-section">
                <h2>The <code>required</code> Attribute</h2>
                <p>
                    The simplest validation: mark fields as mandatory. Forms cannot be submitted until required fields are filled.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Required Fields Example</span>
                    </div>
                    <CodeBlock language="html" code={`<form>
    <label for="username">Username (required):</label>
    <input type="text" id="username" name="username" required>

    <label for="email">Email (required):</label>
    <input type="email" id="email" name="email" required>

    <button type="submit">Submit</button>
</form>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Behavior:</strong> If you try to submit with empty required fields, the browser shows
                        a validation message like "Please fill out this field" and prevents submission.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Input Type Validation</h2>
                <p>
                    HTML5 input types provide automatic format validation:
                </p>

                <div className="input-types-showcase">
                    <div className="input-type-card">
                        <h3><code>type="email"</code></h3>
                        <p><strong>Validates:</strong> Email format (contains @ and valid structure)</p>
                        <p><strong>Mobile keyboard:</strong> Shows @ and .com keys</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="email" name="user_email" required>`} />
                        </div>
                        <p className="validation-message"> Error: "Please include an '@' in the email address"</p>
                    </div>

                    <div className="input-type-card">
                        <h3><code>type="url"</code></h3>
                        <p><strong>Validates:</strong> URL format (must include protocol like http://)</p>
                        <p><strong>Mobile keyboard:</strong> Shows / and .com keys</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="url" name="website" placeholder="https://example.com">`} />
                        </div>
                        <p className="validation-message"> Error: "Please enter a URL"</p>
                    </div>

                    <div className="input-type-card">
                        <h3><code>type="tel"</code></h3>
                        <p><strong>Validates:</strong> None (phone formats vary globally)</p>
                        <p><strong>Mobile keyboard:</strong> Shows numeric keypad</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="tel" name="phone" pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" placeholder="123-456-7890">`} />
                        </div>
                    </div>

                    <div className="input-type-card">
                        <h3><code>type="number"</code></h3>
                        <p><strong>Validates:</strong> Numeric input only</p>
                        <p><strong>Mobile keyboard:</strong> Shows numeric keypad with +/- buttons</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="number" name="age" min="18" max="120" required>`} />
                        </div>
                        <p className="validation-message"> Error: "Value must be less than or equal to 120"</p>
                    </div>

                    <div className="input-type-card">
                        <h3><code>type="date"</code></h3>
                        <p><strong>Validates:</strong> Valid date format</p>
                        <p><strong>UI:</strong> Native date picker widget</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="date" name="birthday" min="1900-01-01" max="2025-12-31">`} />
                        </div>
                    </div>

                    <div className="input-type-card">
                        <h3><code>type="range"</code></h3>
                        <p><strong>Validates:</strong> Value within min/max range</p>
                        <p><strong>UI:</strong> Slider control</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="range" name="volume" min="0" max="100" value="50">`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Length Constraints</h2>
                <p>
                    Control minimum and maximum input length:
                </p>

                <div className="attributes-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Purpose</th>
                                <th>Works With</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>minlength</code></td>
                                <td>Minimum characters required</td>
                                <td>text, search, url, tel, email, password</td>
                            </tr>
                            <tr>
                                <td><code>maxlength</code></td>
                                <td>Maximum characters allowed</td>
                                <td>text, search, url, tel, email, password</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Length Validation Example</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Password must be at least 8 characters --&gt;
<label for="password">Password (min 8 chars):</label>
<input type="password" id="password" name="password" minlength="8" required>

<!-- Username max 20 characters --&gt;
<label for="username">Username (max 20 chars):</label>
<input type="text" id="username" name="username" maxlength="20" required>

<!-- Bio limited to 500 characters --&gt;
<label for="bio">Bio (max 500 chars):</label>
<textarea id="bio" name="bio" maxlength="500" rows="4"></textarea>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Note:</strong> <code>maxlength</code> prevents typing beyond the limit.
                        <code>minlength</code> shows an error on submit if too short.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Numeric Constraints</h2>
                <p>
                    For <code>type="number"</code>, <code>type="range"</code>, and date inputs:
                </p>

                <div className="attributes-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Purpose</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>min</code></td>
                                <td>Minimum value</td>
                                <td><code>&lt;input type="number" min="1"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>max</code></td>
                                <td>Maximum value</td>
                                <td><code>&lt;input type="number" max="100"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>step</code></td>
                                <td>Valid increments</td>
                                <td><code>&lt;input type="number" step="0.1"&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Numeric Constraints Example</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Age between 18 and 120 --&gt;
<label for="age">Age (18-120):</label>
<input type="number" id="age" name="age" min="18" max="120" required>

<!-- Price in increments of $0.50 --&gt;
<label for="price">Price:</label>
<input type="number" id="price" name="price" min="0" step="0.50" value="9.99">

<!-- Rating slider 1-5 --&gt;
<label for="rating">Rating (1-5):</label>
<input type="range" id="rating" name="rating" min="1" max="5" value="3">

<!-- Birth date between 1900 and today --&gt;
<label for="birthdate">Birth Date:</label>
<input type="date" id="birthdate" name="birthdate" min="1900-01-01" max="2025-12-31">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Pattern Matching with Regular Expressions</h2>
                <p>
                    The <code>pattern</code> attribute allows custom validation using regular expressions:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Pattern Validation Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- US Phone Number: XXX-XXX-XXXX --&gt;
<label for="phone">Phone (XXX-XXX-XXXX):</label>
<input type="tel" id="phone" name="phone" 
       pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
       placeholder="123-456-7890"
       title="Format: 123-456-7890">

<!-- Postal Code: 5 digits --&gt;
<label for="zip">ZIP Code (5 digits):</label>
<input type="text" id="zip" name="zip" 
       pattern="[0-9]{5}"
       placeholder="12345"
       title="Enter a 5-digit ZIP code">

<!-- Username: 3-16 alphanumeric characters --&gt;
<label for="username">Username (3-16 chars, letters/numbers only):</label>
<input type="text" id="username" name="username"
       pattern="[a-zA-Z0-9]{3,16}"
       title="3-16 characters, letters and numbers only">

<!-- Hexadecimal Color Code --&gt;
<label for="color">Hex Color Code:</label>
<input type="text" id="color" name="color"
       pattern="#[0-9A-Fa-f]{6}"
       placeholder="#FF5733"
       title="Format: #RRGGBB (e.g., #FF5733)">`} />
                </div>

                <div className="regex-patterns">
                    <h3>Common Regex Patterns:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Pattern</th>
                                <th>Validates</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>[0-9]{`{5}`}</code></td>
                                <td>Exactly 5 digits</td>
                            </tr>
                            <tr>
                                <td><code>[A-Za-z]{`{2,10}`}</code></td>
                                <td>2-10 letters only</td>
                            </tr>
                            <tr>
                                <td><code>[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{`{2,}`}</code></td>
                                <td>Email address</td>
                            </tr>
                            <tr>
                                <td><code>https?://.+</code></td>
                                <td>URL starting with http:// or https://</td>
                            </tr>
                            <tr>
                                <td><code>[0-9]{`{3}`}-[0-9]{`{2}`}-[0-9]{`{4}`}</code></td>
                                <td>SSN format: XXX-XX-XXXX</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="tip-box">
                    <strong> Pro Tip:</strong> Always include a <code>title</code> attribute with <code>pattern</code>.
                    The title text appears in the validation error message to guide users.
                </div>
            </div>

            <div className="content-section">
                <h2>Custom Validation Messages</h2>
                <p>
                    While HTML5 provides default messages, you can customize them with JavaScript:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Custom Messages with JavaScript</span>
                    </div>
                    <CodeBlock language="html" code={`<form id="myForm">
    <label for="email">Email:</label>
    <input type="email" id="email" name="email" required>
    
    <button type="submit">Submit</button>
</form>

<script>
const emailInput = document.getElementById('email');

emailInput.addEventListener('invalid', (e) => {
    if (emailInput.validity.valueMissing) {
        emailInput.setCustomValidity('Hey! Email is required.');
    } else if (emailInput.validity.typeMismatch) {
        emailInput.setCustomValidity('Please enter a valid email address.');
    }
});

// Clear custom message on input
emailInput.addEventListener('input', () => {
    emailInput.setCustomValidity('');
});
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Additional Form Attributes</h2>

                <div className="attributes-showcase">
                    <div className="attribute-card">
                        <h3><code>placeholder</code></h3>
                        <p>Hint text that disappears when user starts typing</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" placeholder="Enter your name">`} />
                        </div>
                        <Warning><strong> Don't use placeholder as a label!</strong> Use <code>&lt;label&gt;</code> for accessibility.</Warning>
                    </div>

                    <div className="attribute-card">
                        <h3><code>readonly</code></h3>
                        <p>User can see but not edit the value</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" value="Cannot edit this" readonly>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>disabled</code></h3>
                        <p>Field is grayed out and not submitted with form</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" value="Disabled field" disabled>`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>autofocus</code></h3>
                        <p>Automatically focuses this field on page load</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="text" name="search" autofocus>`} />
                        </div>
                        <Warning><strong> Use sparingly!</strong> Can be disorienting for screen reader users.</Warning>
                    </div>

                    <div className="attribute-card">
                        <h3><code>autocomplete</code></h3>
                        <p>Controls browser autofill behavior</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="email" name="email" autocomplete="email">
<input type="password" name="password" autocomplete="current-password">
<input type="text" name="search" autocomplete="off">`} />
                        </div>
                    </div>

                    <div className="attribute-card">
                        <h3><code>multiple</code></h3>
                        <p>Allow multiple values (for email, file inputs)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<input type="file" name="photos" multiple>
<input type="email" name="recipients" multiple>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Form Validation Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Registration Form with Full Validation</span>
                    </div>
                    <CodeBlock language="html" code={`<form action="/register" method="POST">
    <!-- Username: 3-16 alphanumeric characters --&gt;
    <label for="username">Username *</label>
    <input type="text" 
           id="username" 
           name="username" 
           pattern="[a-zA-Z0-9]{3,16}"
           title="3-16 characters, letters and numbers only"
           required>

    <!-- Email validation --&gt;
    <label for="email">Email *</label>
    <input type="email" 
           id="email" 
           name="email" 
           required>

    <!-- Password: minimum 8 characters --&gt;
    <label for="password">Password *</label>
    <input type="password" 
           id="password" 
           name="password" 
           minlength="8"
           title="Minimum 8 characters"
           required>

    <!-- Age: 18-120 --&gt;
    <label for="age">Age *</label>
    <input type="number" 
           id="age" 
           name="age" 
           min="18" 
           max="120"
           title="Must be 18 or older"
           required>

    <!-- Website (optional) --&gt;
    <label for="website">Website</label>
    <input type="url" 
           id="website" 
           name="website" 
           placeholder="https://example.com">

    <!-- Terms agreement --&gt;
    <label>
        <input type="checkbox" name="terms" required>
        I agree to the Terms and Conditions *
    </label>

    <button type="submit">Register</button>
</form>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Disabling Validation</h2>
                <p>
                    Sometimes you need to bypass validation (e.g., "Save Draft" button):
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Bypass Validation Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<form>
    <input type="email" name="email" required>
    
    <!-- This button validates --&gt;
    <button type="submit">Publish</button>
    
    <!-- This button bypasses validation --&gt;
    <button type="submit" formnovalidate>Save Draft</button>
</form>

<!-- Disable validation for entire form --&gt;
<form novalidate>
    <input type="email" name="email" required>
    <button type="submit">Submit (no validation)</button>
</form>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Always validate on the server (client-side can be bypassed)</li>
                            <li>Use appropriate input types for better UX</li>
                            <li>Provide clear error messages with <code>title</code></li>
                            <li>Use labels for every input (accessibility)</li>
                            <li>Test forms with keyboard-only navigation</li>
                            <li>Indicate required fields visually (*)</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Rely solely on client-side validation for security</li>
                            <li>Use placeholder as a replacement for labels</li>
                            <li>Make validation messages cryptic or technical</li>
                            <li>Disable paste functionality in password fields</li>
                            <li>Use <code>autofocus</code> on every page</li>
                            <li>Validate on every keystroke (use blur or submit)</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLForms;


